import React, { useEffect } from 'react';
import { useIntl } from 'umi';
import { omit } from 'lodash-es';
// 内部工具
import { storage } from '@/utils/utils';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
// 服务和类型
import { DSAgentModelResponse } from '@/pages/aigc/pages/Agent/service/service';
// 组件
import Loading from '@/pages/intelligence/components/Loading';
import DSExpertAgentCard from '@/pages/aigc/pages/Agent/Home/components/DSExpertAgentCard';
import CopilotCard from '@/pages/aigc/pages/Agent/Home/components/CopilotCard';
// 资源
import Robot from '../../../svg/robot.svg';
// 样式
import './index.scss';

export interface ISelectedDsModelType {
  index: number;
  name: string;
  modelId: string;
  modelName: string;
  locale: string;
  startupPrompt: string;
  agentBanner?: string;
}

// 组件展示类型：home（首页大卡片）| copilot（侧边栏小卡片）
export type DSAgentVariant = 'home' | 'copilot';

interface IDSAgentWrapperProps {
  /** 组件展示类型：home（首页大卡片）| copilot（侧边栏小卡片），默认 home */
  variant?: DSAgentVariant;
  /** 当前选中的模型 */
  currentSelectedDsModel?: ISelectedDsModelType;
  /** 设置选中模型的方法 */
  setCurrentSelectedDsModel?: React.Dispatch<React.SetStateAction<ISelectedDsModelType | undefined>>;
  /** DS Agent 数据列表（外部传入） */
  dSExpertAgent: DSAgentModelResponse['agent_models'][];
  /** 数据加载状态（外部传入） */
  loading?: boolean;
  /** 底部输入框区域高度（Copilot 模式下动态计算，避免列表被遮盖） */
  inputAreaHeight?: number;
}

const DSAgentWrapper = ({
  variant = 'home',
  currentSelectedDsModel,
  setCurrentSelectedDsModel,
  dSExpertAgent,
  loading = false,
  inputAreaHeight,
}: IDSAgentWrapperProps) => {
  const { formatMessage, locale } = useIntl();
  const isCopilot = variant === 'copilot';
  
  useEffect(() => {
    // 每次切换，先清空，防止数据短暂停留
    setCurrentSelectedDsModel?.(undefined);

    if (dSExpertAgent && dSExpertAgent.length > 0) {
      const selectedSample = storage.getItem('__dba_ds_selected_sample') as ISelectedDsModelType;
      const matchModel = dSExpertAgent.find((model: DSAgentModelResponse['agent_models']) => model?.model_id === selectedSample?.modelId);
      const value: ISelectedDsModelType = {
        index: selectedSample?.index ?? -1,
        name: matchModel?.title || '',
        modelId: matchModel?.model_id || '',
        modelName: selectedSample?.modelName || '',
        locale,
        startupPrompt: matchModel?.startup_prompt || '',
        agentBanner: matchModel?.agent_banner,
      };
      storage.setItem('__dba_ds_selected_sample', omit(value, ['agentBanner']));
      setCurrentSelectedDsModel?.(value);
    }
  }, [dSExpertAgent, locale, setCurrentSelectedDsModel]);

  useEffect(() => {
    if (dSExpertAgent?.length === 0) {
      return;
    }

    const initSelectedSample = storage.getItem('__dba_ds_selected_sample') as ISelectedDsModelType;
    const firstHasPermissionModel = dSExpertAgent?.find((model: DSAgentModelResponse['agent_models']) => model.has_permission);
    const firstDsModel: ISelectedDsModelType = {
      index: dSExpertAgent?.findIndex((model: DSAgentModelResponse['agent_models']) => model.title === firstHasPermissionModel?.title) ?? -1,
      name: firstHasPermissionModel?.title || '',
      modelId: firstHasPermissionModel?.model_id || '',
      modelName: firstHasPermissionModel?.model_name || '',
      locale,
      startupPrompt: firstHasPermissionModel?.startup_prompt || '',
      agentBanner: firstHasPermissionModel?.agent_banner,
    };
    // 判断initSelectedSample?.modelId 对应的dSExpertAgent是否有权限has_permission, 防止原先有权限， 但是后面设置无权限，此时缓存会直接读取无权限的数据，命中有误
    const hasPermission = dSExpertAgent?.find((model: DSAgentModelResponse['agent_models']) => model?.model_id === initSelectedSample?.modelId)?.has_permission;
    if (!initSelectedSample?.modelId || !hasPermission) {
      storage.setItem('__dba_ds_selected_sample', omit(firstDsModel, ['agentBanner']));
      setCurrentSelectedDsModel?.(firstDsModel);
    }
  }, [dSExpertAgent, setCurrentSelectedDsModel]);

  // 通用的卡片点击处理逻辑
  const handleCardClick = (agent: DSAgentModelResponse['agent_models'], index: number) => {
    const value: ISelectedDsModelType | undefined = currentSelectedDsModel?.modelId === agent.model_id
      ? undefined
      : {
        index,
        name: agent.title || '',
        modelId: agent.model_id || '',
        modelName: agent.model_name || '',
        locale,
        startupPrompt: agent.startup_prompt || '',
        agentBanner: agent?.agent_banner,
      };

    if (agent.has_permission) {
      storage.setItem('__dba_ds_selected_sample', omit(value, ['agentBanner']));
      // 仅在 home 模式下埋点
      if (!isCopilot) {
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501016',
          extInfo: JSON.stringify({
            message: agent.model_id,
            name: agent.title,
          }),
          typeId: 'aigc',
        });
      }
      setCurrentSelectedDsModel?.(value);
    }
  };

  // Copilot 侧边栏样式：保持容器结构稳定，仅在卡片列表区域显示 loading，避免布局抖动
  if (isCopilot) {
    // 动态计算：输入框高度 + bottom定位(20px) + 安全余量(20px) + 上方区域（tab 62px + 欢迎语约100px + 标题约45px）
    const topOffset = 315; // tab区域 + 欢迎语 + 模板标题
    const bottomOffset = (inputAreaHeight || 90) + 40;
    // 内嵌模式(docked)下面板不在视口顶部，动态计算面板距离视口顶部的实际偏移量
    // 不同系统（home vs 其他）的布局差异导致导航栏高度不同，硬编码无法覆盖所有场景
    const dockedPanel = document.querySelector<HTMLElement>('.dbh-agent-copilot--docked');
    const navOffset = dockedPanel ? dockedPanel.getBoundingClientRect().top - 75 : 0;
    return (
      <div
        className="dba-copilot-ds-templates-section"
        style={{ maxHeight: `calc(100vh - ${navOffset + topOffset + bottomOffset}px)` }}
      >
        <div className="dba-copilot-ds-templates-title">
          <img src={Robot} alt="Robot Icon" className="dba-copilot-ds-templates-title-icon" />
          <span>{formatMessage({ id: 'dba_ds_template_select_title' })}</span>
        </div>
        {loading ? (
          <Loading style={{ minHeight: 200 }} />
        ) : (
          <div className="dba-copilot-ds-templates-list">
            {dSExpertAgent?.map((agent: DSAgentModelResponse['agent_models'], index: number) => (
              <CopilotCard
                key={`${index + 1}. ${agent.model_name}`}
                title={agent.title}
                content={agent.description}
                hasPermission={agent.has_permission}
                author={{ name: agent.creator, img: agent.creator_img }}
                diagram={agent.agent_banner}
                isSelected={currentSelectedDsModel?.modelId === agent.model_id}
                handleOnClick={() => handleCardClick(agent, index)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Home 首页模式下，loading 仍替换整个组件
  if (loading) return <Loading />;

  // Home 首页样式（默认）
  return (
    <>
      {dSExpertAgent?.length >= 3 && (
        <div className="dba-section-title">
          <span className="dba-section-title-line" />
          <div className="dba-section-title-content">
            <img src={Robot} alt="Robot Icon" className="dba-section-title-content-icon" />
            <span className="dba-section-title-content-gradient-text">
              {formatMessage({ id: 'dba_ds_agent_section_title' })}
            </span>
          </div>
          <span className="dba-section-title-line" />
        </div>
      )}

      <div className="dba-ds-section-cards-wrapper">
        <div className="dba-ds-section-cards-wrapper-content">
          {dSExpertAgent?.map((agent: DSAgentModelResponse['agent_models'], index: number) => (
            <DSExpertAgentCard
              key={`${index + 1}. ${agent.model_name}`}
              title={agent.title}
              content={agent.description}
              hasPermission={agent.has_permission}
              author={{ name: agent.creator, img: agent.creator_img }}
              diagram={agent.agent_banner}
              isSelected={currentSelectedDsModel?.modelId === agent.model_id}
              handleOnClick={() => handleCardClick(agent, index)}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default DSAgentWrapper;
